import React, { forwardRef, useImperativeHandle, useRef } from 'react';

import { Formik, Form, FormikProps } from 'formik';

import { durationSecondsToString, formatSpeed } from 'ff/debrief/utils';
import { useCurrentAircraft, useCurrentTracklog } from 'ff/debrief/hooks';
import {
    TracklogOtherInfoEditableUpdate,
    TracklogOtherInfoEditable,
} from 'ff/debrief/features/TracklogApi';
import InfoDivider from './InfoDivider';
import InfoRow from './InfoRow';
import NotesRow from './NotesRow';
import { List } from './styledInfoComponents';
import { isFeatureEnabled } from 'ff/common/ffAppUtils';
import AssociationList from 'ff/debrief/components/logbookAssociation/AssociationList';

interface Props {
    editable?: boolean;
    isEditing?: boolean;
}

export type InfoTableRef = {
    reset: () => void;
    save: () => Promise<void>;
};

const InfoTable = forwardRef<InfoTableRef, Props>((props, ref) => {
    const { editable: isEditable, isEditing } = props;
    const {
        tracklog,
        metadata,
        trimmedValues,
        isOwnTracklog,
        hasAssociation,
        updateOtherInfo,
    } = useCurrentTracklog();

    const { speedUnit } = useCurrentAircraft();

    const userMayEdit = isEditable && isOwnTracklog;

    const formRef = useRef<FormikProps<TracklogOtherInfoEditable>>(null);

    const handleSubmit = () => {
        const changedValues = getUpdatedFields(
            formRef.current?.initialValues,
            formRef.current?.values
        );
        if (Object.keys(changedValues).length === 0) {
            return;
        }
        return updateOtherInfo(changedValues)
            .catch(ex => {
                console.error('Error updating other info', ex);
            })
            .finally(() => {
                formRef.current?.setSubmitting(false);
            });
    };

    const initialSummaryValues: TracklogOtherInfoEditable = {
        userProvidedName: tracklog.name || null,
        pilotName: tracklog.pilotName || null,
        tailNumber: tracklog.tailNumber || null,
        derivedOrigin: tracklog.derivedOrigin || null,
        derivedDestination: tracklog.derivedDestination || null,
        userRemarks: metadata.userRemarks || null,
    };

    const getUpdatedFields = (
        initialValues: TracklogOtherInfoEditable,
        maybeUpdatedValues: TracklogOtherInfoEditableUpdate
    ): TracklogOtherInfoEditableUpdate => {
        const keys = Object.keys(initialValues) as Array<
            keyof TracklogOtherInfoEditable
        >;
        const changedFields: TracklogOtherInfoEditableUpdate = {};
        return keys.reduce((acc, key) => {
            if (initialValues[key] !== maybeUpdatedValues[key]) {
                acc[key] = maybeUpdatedValues[key];
            }
            return acc;
        }, changedFields);
    };

    useImperativeHandle(ref, () => ({
        reset: () => {
            formRef.current?.resetForm();
        },
        save: () => {
            return formRef.current?.submitForm();
        },
    }));
    return (
        <List>
            <Formik
                onSubmit={handleSubmit}
                innerRef={formRef}
                initialValues={initialSummaryValues}
                enableReinitialize
            >
                {({ values: editableValues }) => (
                    <Form>
                        <InfoDivider label="Summary" />
                        <InfoRow
                            label="Track Log Name"
                            value={editableValues.userProvidedName}
                            editable={userMayEdit}
                            editing={isEditing}
                            field="userProvidedName"
                            border
                        ></InfoRow>
                        <InfoRow
                            label="Tail Number"
                            value={editableValues.tailNumber}
                            editable={userMayEdit}
                            editing={isEditing}
                            field="tailNumber"
                            border
                        ></InfoRow>
                        <InfoRow
                            label="Start"
                            value={editableValues.derivedOrigin}
                            editable={userMayEdit}
                            editing={isEditing}
                            field="derivedOrigin"
                            border
                        ></InfoRow>
                        <InfoRow
                            label="End"
                            value={editableValues.derivedDestination}
                            editable={userMayEdit}
                            editing={isEditing}
                            field="derivedDestination"
                            border
                        ></InfoRow>
                        <InfoRow
                            label="Pilot Name"
                            value={editableValues.pilotName || 'Unknown'}
                            editable={userMayEdit}
                            editing={isEditing}
                            field="pilotName"
                            border
                        ></InfoRow>
                        <InfoRow
                            label="Initial GPS Source"
                            value={tracklog.initialGpsSource}
                            border
                        ></InfoRow>
                        <InfoRow
                            label="Recorded On"
                            value={tracklog.recordingDevice}
                        ></InfoRow>

                        <InfoDivider label="Metrics" />
                        <InfoRow
                            label="Flight Time"
                            value={durationSecondsToString(
                                trimmedValues.actualFlightTime
                            )}
                            border
                        ></InfoRow>
                        <InfoRow
                            label="Avg Ground Speed"
                            value={formatSpeed(
                                trimmedValues.avgGndspd,
                                speedUnit
                            )}
                            border
                        ></InfoRow>
                        <InfoRow
                            label="Minimum g Load"
                            showSentryMsg={!trimmedValues.minimumG}
                            value={
                                trimmedValues.minimumG
                                    ? trimmedValues.minimumG.toFixed(2)
                                    : '-'
                            }
                            border
                        ></InfoRow>
                        <InfoRow
                            label="Maximum g Load"
                            showSentryMsg={!trimmedValues.maximumG}
                            value={
                                trimmedValues.maximumG
                                    ? trimmedValues.maximumG.toFixed(2)
                                    : '-'
                            }
                        ></InfoRow>

                        <InfoDivider label="Notes" />
                        <NotesRow
                            value={editableValues.userRemarks}
                            editable={userMayEdit}
                            isEditing={isEditing}
                        />
                        {isFeatureEnabled('logbook_association') &&
                            hasAssociation && (
                                <>
                                    <InfoDivider label="Logbooks Associated" />
                                    <AssociationList />
                                </>
                            )}
                    </Form>
                )}
            </Formik>
        </List>
    );
});
InfoTable.displayName = 'InfoTable';
export default InfoTable;
